import React, { ReactElement, useState } from "react";
import { Button, Heading, RadioGroup } from "@telia/teddy";
import cn from "classnames";
import _find from "lodash/find";
import _map from "lodash/map";
import "./OnboardingMobileSupportModal.less";
import { connect, useDispatch } from "react-redux";
import { RootState } from "../../../../../../models";
import { OnboardingSupportInfo } from "../../../../../../models/state";
import { addOnboardingMobileSupport } from "../../../../checkout/state/actions/cartActions";
import { cartItemIdToAddOnboardingService } from "../../../../checkout/state/selectors";
import { getSelectedOnboardingSku } from "../../../../checkout/state/selectors/deliveryInfoSelectors";
import { trackWebshopClickEvent } from "../../../../google-analytics/googleAnalyticsEventHelper";
import { InfoModal, InfoModalBody } from "../../../../components";
import OnboardingMobileSupportItem from "../OnboardingMobileSupportItem/OnboardingMobileSupportItem";

interface StateProps {
    cartItemId: string | undefined;
    selectedOnboardingSku: string;
}

interface OnboardingMobileSupportModalProps extends StateProps {
    onboardingMobileSupportInfo: OnboardingSupportInfo;
    setShowModal: (show: boolean) => void;
    showModal: boolean;
}

const OnboardingMobileSupportModal = ({
    showModal,
    setShowModal,
    onboardingMobileSupportInfo,
    cartItemId,
    selectedOnboardingSku,
}: OnboardingMobileSupportModalProps): ReactElement | null => {
    const initialOnboardingMobileSupport = _find(
        onboardingMobileSupportInfo.services,
        (service) => service.sku === selectedOnboardingSku,
    );
    const dispatch = useDispatch();
    const [selectedOnboardingMobileSupport, setSelectedOnboardingMobileSupport] =
        useState(initialOnboardingMobileSupport);
    const [onboardingMobileSupportAdded, setOnboardingMobileSupportAdded] = useState(false);

    const onAddOnboardingMobileSupport = async () => {
        if (cartItemId && selectedOnboardingMobileSupport) {
            try {
                dispatch(addOnboardingMobileSupport(cartItemId, selectedOnboardingMobileSupport.sku)).then(() => {
                    setOnboardingMobileSupportAdded(true);
                    trackWebshopClickEvent({
                        action: "click",
                        text: "Legg til starthjelp",
                        type: "button",
                        url: "/kasse/oppsummering",
                        context: "add_starthjelp_option",
                    });
                });
            } catch (err) {
                setOnboardingMobileSupportAdded(false);
            }
        }
    };

    function onContinue(): void {
        setOnboardingMobileSupportAdded(false);
        setShowModal(false);
    }

    const items = _map(onboardingMobileSupportInfo.services, (item) => {
        return (
            <OnboardingMobileSupportItem
                item={item}
                key={item.code}
                isSelected={selectedOnboardingMobileSupport?.sku === item.sku}
            />
        );
    });

    const selectedValue = selectedOnboardingMobileSupport?.code || "";
    const onSupportChange = (code: string): void => {
        const selectedSupport = _find(onboardingMobileSupportInfo.services, (service) => service.code === code);
        if (selectedSupport) {
            setSelectedOnboardingMobileSupport(selectedSupport);
        }
    };

    return (
        <InfoModal
            open={showModal}
            setOpen={(): void => setShowModal(!showModal)}
            size="large"
            ariaLabelledBy="modal10-label"
            className={cn("onboarding-mobile-support-modal--container")}
        >
            <InfoModalBody>
                {!onboardingMobileSupportAdded && (
                    <div className="onboarding-mobile-support-modal">
                        <Heading as="h3" variant="title-100">
                            {onboardingMobileSupportInfo.heading}
                        </Heading>
                        <div
                            className="onboarding-mobile-support-modal__ingress"
                            dangerouslySetInnerHTML={{ __html: onboardingMobileSupportInfo.ingress }}
                        />
                        {items.length > 0 && (
                            <RadioGroup value={selectedValue} onValueChange={onSupportChange} name="mobileSupport">
                                {items}
                            </RadioGroup>
                        )}
                        <div className="onboarding-mobile-support-modal__button-container">
                            <Button
                                className="onboarding-mobile-support-modal__button"
                                variant="secondary"
                                onClick={() => setShowModal(false)}
                            >
                                Jeg trenger ikke hjelp
                            </Button>
                            <Button
                                className="onboarding-mobile-support-modal__button"
                                variant="primary"
                                onClick={() => onAddOnboardingMobileSupport()}
                            >
                                Legg til starthjelp
                            </Button>
                        </div>
                    </div>
                )}
                {onboardingMobileSupportAdded && selectedOnboardingMobileSupport && (
                    <div className="onboarding-mobile-support-modal">
                        {selectedOnboardingMobileSupport.disclaimerText && (
                            <div dangerouslySetInnerHTML={{ __html: selectedOnboardingMobileSupport.disclaimerText }} />
                        )}
                        <Button variant="primary" onClick={() => onContinue()}>
                            Lukk
                        </Button>
                    </div>
                )}
            </InfoModalBody>
        </InfoModal>
    );
};

const mapStateToProps = (state: RootState): StateProps => ({
    cartItemId: cartItemIdToAddOnboardingService(state),
    selectedOnboardingSku: getSelectedOnboardingSku(state),
});

export default connect(mapStateToProps)(OnboardingMobileSupportModal);
